import URI from 'urijs';

const defaultConfigs = {
  scopes: [
    'openid',
    'athena/user/Identity.Demographics.read',
    'athena/user/Identity.Account.Factors.write',
    'athena/user/Identity.Account.Invites.write',
    'athena/user/Identity.Account.Profile.write',
    'athena/user/Identity.Account.Profile.read',
    'athena/user/Identity.LwaConfig.PostAuthn.read',
    'athena/user/Identity.Account.Profile.delete',
    'okta.myAccount.phone.read',
    'okta.myAccount.phone.manage',
  ],
  redirectUri: 'oauth2/v1/login_callback_lwa_spa',
  loginThrottleLimits: [ [ 5, 60 ] ],
  anetUserDomain: 'managedidentity.platform.athenahealth.com',
  logoUrl: process.env.REACT_APP_LOGO_URL,
  oktaIdpDiscovery: false,
  showPasswordToggleOnSignInPage: true,
  fhirScopes: [ 'launch', 'launch/patient', 'launch/encounter', 'fhirUser' ],
  fhirPatientScopes: [ 'launch/patient' ],
  fhirEhrLaunchScopes: [ 'launch', 'fhirUser' ],
  rolloutAuthenticatorAppFactor: true,
};

const nonProdAmplitudeApiKey = '20dc2a80bf07774d9fc999f614e9ea5e';
const preProdAmplitudeApiKey = 'e83bc020b299658e1e1c454fc9616d92';
const prodAmplitudeApiKey = 'c41bf44737b4598dcae4c0eaec9b5833';

const defaultOktaUrl = process.env.REACT_APP_OKTA_URL || 'https://identity.dev.athenahealth.com/';
const defaultAuthServerBaseUrl = new URI(defaultOktaUrl).segment('oauth2')
  .toString();

const localDevConfig = {
  primaryAuthorizationServer: {
    baseUrl: defaultAuthServerBaseUrl,
    id: process.env.REACT_APP_OKTA_AUTHSERVER_ID || 'aus13kbtj4HAdI3451t7',
  },
  oauthClientId: process.env.REACT_APP_OAUTH_CLIENT_ID || '0oa15p00avlpi6ji61t8',
  oktaURL: defaultOktaUrl,
  oktaOrgId: process.env.REACT_APP_OKTA_ORG_ID || '00oot8nf9c7ovmdGX1t6',
  iamCoreUrl: process.env.REACT_APP_IAM_CORE_SERVER_URL || 'https://iam-core.uiam:20020/',
  amplitudeApiKey: nonProdAmplitudeApiKey,
  loggingService: 'http://localhost:8080/api/v1/local/lwa-logger',
  // loginThrottleLimits - Array of arrays where the first number is the max number of logins to allow
  // and the second number is the time frame in seconds to enforce that
  // below commented out value left as an example how you could have multiple rules
  // [ [2, 60], [5, 180] ],
  loginThrottleLimits: [ [ 5, 60 ] ],
  manageProfileAppUrl: 'https://lwa-user-profile-management.athenahealth.com:33000/',
  ...defaultConfigs,
};

const awsDevConfig = {
  primaryAuthorizationServer: {
    baseUrl: 'https://identity.dev.athenahealth.com/oauth2/',
    id: 'aus13kbtj4HAdI3451t7',
  },
  oauthClientId: '0oa15p00avlpi6ji61t8',
  oktaURL: 'https://identity.dev.athenahealth.com/',
  oktaOrgId: '00oot8nf9c7ovmdGX1t6',
  iamCoreUrl: 'https://identity.dev.athena.io/identity/',
  amplitudeApiKey: nonProdAmplitudeApiKey,
  loggingService: 'https://identity.dev.athena.io/uiam-metrics/api/v1/dev/lwa-logger',
  manageProfileAppUrl: 'https://myidentity-dev-profile.platform.athenahealth.com',
};

const intConfig = {
  primaryAuthorizationServer: {
    baseUrl: 'https://identity.int.athenahealth.com/oauth2/',
    id: 'ausc16vzbfqYFn4jP0h7',
  },
  oauthClientId: '0oa2czeng5zJZ53Fw0h8',
  oktaURL: 'https://identity.int.athenahealth.com/',
  oktaOrgId: '00o9o9irbsIlOt9Oj0h7',
  iamCoreUrl: 'https://identity.int.athena.io/identity/',
  amplitudeApiKey: nonProdAmplitudeApiKey,
  loggingService: 'https://identity.int.athena.io/uiam-metrics/api/v1/int/lwa-logger',
  manageProfileAppUrl: 'https://myidentity-int-profile.platform.athenahealth.com',
};

const preprodConfig = {
  primaryAuthorizationServer: {
    baseUrl: 'https://identity.preprod.athenahealth.com/oauth2/',
    id: 'ausv45j1o7FKYZbu1297',
  },
  oauthClientId: '0oaxgtua2rwQg9HJA297',
  oktaURL: 'https://identity.preprod.athenahealth.com/',
  oktaOrgId: '00oub22wrzFBeScC3297',
  iamCoreUrl: 'https://identity.preprod.athena.io/identity/',
  amplitudeApiKey: preProdAmplitudeApiKey,
  loggingService: 'https://identity.preprod.athena.io/uiam-metrics/api/v1/preprod/lwa-logger',
  manageProfileAppUrl: 'https://myidentity-preprod-profile.platform.athenahealth.com',
};

const prodPrimaryAuthServer = {
  baseUrl: 'https://identity.athenahealth.com/oauth2/',
  id: 'auset0ja9xZ2Hniep296',
};
const prodConfig = {
  primaryAuthorizationServer: prodPrimaryAuthServer,
  oauthClientId: '0oawxcmh2iNaGSIOt297',
  oktaURL: 'https://identity.athenahealth.com/',
  oktaOrgId: '00o6wzsavf7U5zl4S296',
  iamCoreUrl: 'https://identity.athena.io/identity/',
  amplitudeApiKey: prodAmplitudeApiKey,
  loggingService: 'https://identity.athena.io/uiam-metrics/api/v1/prod/lwa-logger',
  manageProfileAppUrl: 'https://myidentity-profile.platform.athenahealth.com',
  rolloutAuthenticatorAppFactor: true,
};

const supportPhiConfig = {
  primaryAuthorizationServer: {
    baseUrl: 'https://identity.athenahealth.com/oauth2/',
    id: 'ausu5cwvrZnWYasjS296',
  },
  oauthClientId: '0oawxcn46kyxQuSSS297',
  oktaURL: 'https://identity.athenahealth.com/',
  oktaOrgId: '00o6wzsavf7U5zl4S296',
  iamCoreUrl: 'https://identity.preview.api.athena.io/identity/',
  amplitudeApiKey: prodAmplitudeApiKey,
  loggingService: 'https://identity.athena.io/uiam-metrics/api/v1/support-phi/lwa-logger',
  manageProfileAppUrl: 'https://myidentity-support-profile.platform.athenahealth.com',
  rolloutAuthenticatorAppFactor: true,
};

const supportNonPhiConfig = {
  primaryAuthorizationServer: {
    baseUrl: 'https://identity.athenahealth.com/oauth2/',
    id: 'aus1eumlr5MD3rYcn297',
  },
  oauthClientId: '0oawxcngbjquascDI297',
  oktaURL: 'https://identity.athenahealth.com/',
  oktaOrgId: '00o6wzsavf7U5zl4S296',
  iamCoreUrl: 'https://identity.salesdemo.api.athena.io/identity/',
  amplitudeApiKey: prodAmplitudeApiKey,
  loggingService: 'https://identity.athena.io/uiam-metrics/api/v1/support-non-phi/lwa-logger',
  manageProfileAppUrl: 'https://myidentity-support-demo-profile.platform.athenahealth.com',
  rolloutAuthenticatorAppFactor: true,
};

const envConfigMappings = {
  'http://localhost:3000': localDevConfig,
  'https://myidentity-dev.aws.athenahealth.com': awsDevConfig,
  'https://myidentity-int.aws.athenahealth.com': intConfig,
  'https://myidentity-preprod.aws.athenahealth.com': preprodConfig,
  'https://myidentity.platform.athenahealth.com': prodConfig,
  'https://myidentity-support.platform.athenahealth.com': supportPhiConfig,
  'https://myidentity-support-demo.platform.athenahealth.com': supportNonPhiConfig,
  'https://login.dev.platform.athenahealth.com:3000': localDevConfig,
};

export const envConfig = Object.keys(envConfigMappings).reduce((config, key) => {
  config[key] = {
    ...defaultConfigs,
    ...envConfigMappings[key],
  };
  return config;
}, {});

const config = envConfig[window.location.origin] || localDevConfig;
export default config;
